/**
 * DV-STAGE-001: Prompt Preview Component
 * Shows rendered prompt preview with validation and diff capabilities
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Eye, EyeOff, Copy, RefreshCw, Hash, AlertTriangle, Check } from 'lucide-react';
import { cn } from '@/utils/cn';
import { getApiHeadersJson, buildApiUrl, API_ENDPOINTS } from '@/lib/api-base';
import { redactPromptForCopy } from '@/lib/redaction';
import { type LogEntry } from './LogsPanel';
import { type StageId } from './StageCard';

export interface PromptPreviewData {
  rendered: string;
  variables: number;
  missingKeys: string[];
  metadata: {
    templateName: string;
    lastUpdated: string;
    hash: string;
  };
}

export interface PromptPreviewProps {
  stageId: StageId;
  effectiveInput: any | null;
  promptPreview?: PromptPreviewData | null;
  promptHash?: string | null;
  validationRequestId: string | null;
  onValidate: (requestId: string) => void;
  onLog: (entry: Omit<LogEntry, 'id'>) => void;
  className?: string;
  'data-testid'?: string;
}

// Mock template for client-side rendering (P1)
const MOCK_TEMPLATES: Record<StageId, string> = {
  script: `Generate a video script for {{topic}}.

Duration: {{duration}} seconds
Tone: {{tone}}
Target audience: {{audience || "general"}}

Requirements:
- Clear narrative structure
- Engaging introduction
- Key points: {{keyPoints.length}} items
{{#if includeCallToAction}}
- Include call to action
{{/if}}

Begin the script now:`,

  vision: `Create a visual concept for: {{concept}}

Style parameters:
- Visual style: {{style}}
- Color palette: {{colors.join(", ")}}
- Mood: {{mood}}
- Art direction: {{artDirection || "modern"}}

{{#if referenceImages}}
Reference images provided: {{referenceImages.length}}
{{/if}}

Generate detailed visual guidelines:`,

  storyboard: `Create a storyboard with {{sceneCount}} scenes.

Project details:
- Title: {{title}}
- Duration: {{duration}} seconds
- Transition style: {{transitions}}
- Pacing: {{pacing}}

{{#if visualGuidelines}}
Visual guidelines:
{{visualGuidelines}}
{{/if}}

Generate scene-by-scene breakdown:`,

  frames: `Generate frames for video production.

Technical specifications:
- Resolution: {{resolution}}
- Frame rate: {{fps}} fps
- Quality: {{quality}}
- Total frames: {{frameCount}}

{{#if styleGuide}}
Style guide:
{{styleGuide}}
{{/if}}

Process frame generation:`,

  video: `Generate video segments.

Export settings:
- Format: {{format}}
- Codec: {{codec}}
- Bitrate: {{bitrate}}
- Duration: {{duration}} seconds

{{#if audioSettings}}
Audio:
- Sample rate: {{audioSettings.sampleRate}}
- Channels: {{audioSettings.channels}}
{{/if}}

Begin video generation:`,

  qc: `Perform quality control evaluation.

Evaluation criteria:
{{#each criteria}}
- {{this}}: Threshold {{../threshold}}
{{/each}}

Content type: {{contentType}}
{{#if specificRequirements}}
Additional requirements:
{{specificRequirements}}
{{/if}}

Evaluate quality:`,

  export: `Export final assets.

Export formats:
{{#each formats}}
- {{this}}
{{/each}}

Resolutions:
{{#each resolutions}}
- {{this}}
{{/each}}

{{#if deliverySpecs}}
Delivery specifications:
{{deliverySpecs}}
{{/if}}

Process export:`,

  music: `Generate background music.

Music parameters:
- Genre: {{genre}}
- Mood: {{mood}}
- Duration: {{duration}} seconds
- Tempo: {{tempo || "moderate"}}

{{#if instrumentRequirements}}
Instruments: {{instrumentRequirements.join(", ")}}
{{/if}}

Create music composition:`
};

export function PromptPreview({
  stageId,
  effectiveInput,
  promptPreview: _promptPreview,
  promptHash: _promptHash,
  validationRequestId,
  onValidate,
  onLog,
  className,
  'data-testid': testId = `prompt-preview-${stageId}`
}: PromptPreviewProps) {
  const [expanded, setExpanded] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [clientPreview, setClientPreview] = useState<string>('');
  const [lastServerPreview, setLastServerPreview] = useState<string>('');
  const validationTimeoutRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();

  // Generate client-side preview
  const generateClientPreview = useCallback(() => {
    try {
      if (!effectiveInput) {
        setClientPreview('// No input available for preview');
        return;
      }

      const template = MOCK_TEMPLATES[stageId] || 'Template not available for {{stage}}';

      // Simple template substitution (mock implementation)
      let rendered = template;

      // Basic variable substitution
      const variables = rendered.match(/\{\{[^}]+\}\}/g) || [];
      const foundVariables = new Set<string>();

      variables.forEach(variable => {
        const key = variable.replace(/[{}]/g, '').split('||')[0].trim();
        foundVariables.add(key);

        // Simple property access (no deep nesting for P1)
        const value = effectiveInput[key];
        if (value !== undefined) {
          rendered = rendered.replace(variable, String(value));
        }
      });

      setClientPreview(rendered);

      // Debug logging removed to prevent render loop spam

    } catch (error) {
      const errorMessage = `Failed to generate client preview: ${error instanceof Error ? error.message : String(error)}`;
      setClientPreview(`// Error: ${errorMessage}`);

      onLog({
        ts: new Date().toISOString(),
        level: 'error',
        text: `Client prompt preview failed for ${stageId}`,
        meta: {
          stage: stageId,
          error: error instanceof Error ? error.message : String(error),
          source: 'prompt_preview',
        }
      });
    }
  }, [stageId, effectiveInput, onLog]);

  // Debounced server validation
  const validateWithServer = useCallback(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Cancel any ongoing validation request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    validationTimeoutRef.current = setTimeout(async () => {
      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsValidating(true);
      const requestId = `prompt-validate-${Date.now()}`;

      try {
        onLog({
          ts: new Date().toISOString(),
          level: 'info',
          text: `Starting server prompt validation for ${stageId}`,
          meta: {
            stage: stageId,
            requestId,
            source: 'prompt_preview',
          }
        });

        const response = await fetch(buildApiUrl(API_ENDPOINTS.stageTester.prompt(stageId)), {
          method: 'POST',
          credentials: 'include',
          headers: getApiHeadersJson(),
          signal: abortController.signal,
          body: JSON.stringify({
            input: effectiveInput?.data,
            overrides: {}
          }),
        });

        if (!response.ok) {
          throw new Error(`Prompt validation failed: ${response.status} ${response.statusText}`);
        }

        const validationResult = await response.json();

        onValidate(requestId);
        setLastServerPreview(validationResult.prompt || clientPreview);

        onLog({
          ts: new Date().toISOString(),
          level: 'info',
          text: `Server prompt validation completed for ${stageId}`,
          meta: {
            stage: stageId,
            requestId,
            source: 'prompt_preview',
          }
        });

      } catch (error) {
        onLog({
          ts: new Date().toISOString(),
          level: 'error',
          text: `Server prompt validation failed for ${stageId}`,
          meta: {
            stage: stageId,
            requestId,
            error: error instanceof Error ? error.message : String(error),
            source: 'prompt_preview',
          }
        });
      } finally {
        setIsValidating(false);
      }
    }, 400); // Debounced validation for better responsiveness
  }, [stageId, clientPreview, onValidate, onLog]);

  // Update preview when effective input changes
  useEffect(() => {
    generateClientPreview();
  }, [generateClientPreview]);

  // Trigger server validation when client preview changes
  useEffect(() => {
    if (clientPreview && clientPreview !== '// No input available for preview') {
      validateWithServer();
    }
  }, [clientPreview, validateWithServer]);

  // Copy prompt to clipboard (redacted by default)
  const copyPrompt = useCallback(async (useRedaction = true) => {
    const fullText = expanded ? clientPreview : clientPreview.slice(0, 500);
    const textToCopy = useRedaction ? redactPromptForCopy(fullText) : fullText;

    try {
      await navigator.clipboard.writeText(textToCopy);
      onLog({
        ts: new Date().toISOString(),
        level: 'info',
        text: `Prompt copied to clipboard for ${stageId} (${textToCopy.length} chars, ${useRedaction ? 'redacted' : 'raw'})`,
        meta: {
          stage: stageId,
          copiedLength: textToCopy.length,
          redacted: useRedaction,
          source: 'prompt_preview',
        }
      });
    } catch (error) {
      onLog({
        ts: new Date().toISOString(),
        level: 'error',
        text: `Failed to copy prompt for ${stageId}`,
        meta: {
          stage: stageId,
          error: error instanceof Error ? error.message : String(error),
          source: 'prompt_preview',
        }
      });
    }
  }, [stageId, clientPreview, expanded, onLog]);

  // Calculate prompt hash (simple for P1)
  const calculatePromptHash = useCallback((text: string): string => {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).substring(0, 8);
  }, []);

  const currentHash = clientPreview ? calculatePromptHash(clientPreview) : null;
  const variableCount = (clientPreview.match(/\{\{[^}]+\}\}/g) || []).length;
  const missingVariables = (clientPreview.match(/\{\{[^}]+\}\}/g) || [])
    .map(v => v.replace(/[{}]/g, ''))
    .filter(v => !v.includes('||')); // Variables without defaults

  return (
    <div className={cn("space-y-3", className)} data-testid={testId}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4" style={{ color: 'var(--fg-muted)' }} />
            <span
              className="text-xs font-mono"
              style={{
                color: 'var(--fg-muted)',
                fontFamily: 'var(--font-family-mono)',
              }}
            >
              {currentHash || 'no-hash'}
            </span>
          </div>

          <div
            className="text-xs font-mono"
            style={{
              color: 'var(--fg-muted)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            {variableCount} variables
          </div>

          {missingVariables.length > 0 && (
            <div className="flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" style={{ color: 'var(--warning)' }} />
              <span
                className="text-xs"
                style={{ color: 'var(--warning)' }}
              >
                {missingVariables.length} missing
              </span>
            </div>
          )}

          {isValidating && (
            <div className="flex items-center gap-1">
              <RefreshCw className="w-4 h-4 animate-spin" style={{ color: 'var(--accent)' }} />
              <span
                className="text-xs"
                style={{ color: 'var(--accent)' }}
              >
                Validating...
              </span>
            </div>
          )}

          {validationRequestId && !isValidating && (
            <div className="flex items-center gap-1">
              <Check className="w-4 h-4" style={{ color: 'var(--success)' }} />
              <span
                className="text-xs font-mono"
                style={{
                  color: 'var(--success)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                {validationRequestId.slice(-8)}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {lastServerPreview && lastServerPreview !== clientPreview && (
            <button
              onClick={() => setShowDiff(!showDiff)}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                fontFamily: 'var(--font-family-mono)',
              }}
              data-testid={`${testId}-diff-toggle`}
            >
              Diff
            </button>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-default)',
              color: 'var(--fg-primary)',
              fontFamily: 'var(--font-family-mono)',
            }}
            data-testid={`${testId}-expand-toggle`}
          >
            {expanded ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {expanded ? 'Collapse' : 'Expand'}
          </button>

          <button
            onClick={() => copyPrompt(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-default)',
              color: 'var(--fg-primary)',
              fontFamily: 'var(--font-family-mono)',
            }}
            data-testid={`${testId}-copy`}
          >
            <Copy className="w-3 h-3" />
            Copy
          </button>
        </div>
      </div>

      {/* Preview Content */}
      <div
        className="border rounded-lg overflow-hidden"
        style={{ borderColor: 'var(--border-default)' }}
      >
        <div
          className={cn(
            "p-3 font-mono text-sm transition-all",
            expanded ? "h-80 overflow-y-auto" : "h-32 overflow-y-auto"
          )}
          style={{
            backgroundColor: 'var(--bg-app)',
            color: 'var(--fg-primary)',
            fontFamily: 'var(--font-family-mono)',
          }}
          data-testid={`${testId}-content`}
        >
          {showDiff && lastServerPreview ? (
            <div className="space-y-2">
              <div
                className="text-xs font-bold"
                style={{ color: 'var(--warning)' }}
              >
                DIFF: Client vs Server
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div style={{ color: 'var(--fg-muted)' }}>Client:</div>
                  <pre className="whitespace-pre-wrap">{clientPreview.slice(0, 200)}...</pre>
                </div>
                <div>
                  <div style={{ color: 'var(--fg-muted)' }}>Server:</div>
                  <pre className="whitespace-pre-wrap">{lastServerPreview.slice(0, 200)}...</pre>
                </div>
              </div>
            </div>
          ) : (
            <pre className="whitespace-pre-wrap">
              {expanded ? clientPreview : `${clientPreview.slice(0, 500)}${clientPreview.length > 500 ? '\n\n[...truncated]' : ''}`}
            </pre>
          )}
        </div>

        {!expanded && clientPreview.length > 500 && (
          <div
            className="px-3 py-2 border-t text-xs"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-subtle)',
              color: 'var(--fg-muted)',
            }}
          >
            {clientPreview.length} characters total • Click expand to see full prompt
          </div>
        )}
      </div>

      {/* Missing Variables Warning */}
      {missingVariables.length > 0 && (
        <div
          className="p-3 rounded border"
          style={{
            backgroundColor: 'var(--warning)',
            borderColor: 'var(--warning)',
            color: 'var(--bg-app)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">Missing Variables</span>
          </div>
          <div className="text-xs">
            The following variables are not provided in the input:{' '}
            <code className="font-mono">{missingVariables.join(', ')}</code>
          </div>
        </div>
      )}
    </div>
  );
}